import { zonedInstant } from '@/lib/reporting/timezone';
import { CLINIC_TIMEZONE } from '@/utils/calendarClinicTime';

const CIVIL = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A complete calendar day. Month 00 and a truncated "2026-10" are not complete:
 * the native date field emits those while its month segment is being edited.
 */
export function isCompleteCivilDate(value: string): boolean {
  const match = CIVIL.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * `YYYY-MM-DDTHH:mm` as Europe/Copenhagen wall time, or null. Never throws.
 * The clinic clock is not the browser timezone and not UTC.
 */
export function safeLocalDateTime(value: string): Date | null {
  if (!value || value.length < 16) return null;
  const civil = value.slice(0, 10);
  if (!isCompleteCivilDate(civil)) return null;
  const time = value.slice(11, 16);
  if (!/^\d{2}:\d{2}$/.test(time)) return null;
  const [year, month, day] = civil.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  const date = zonedInstant(year, month, day, hour, minute, 0, 0, CLINIC_TIMEZONE);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/** ISO instant, or null when the date is incomplete. `Invalid Date#toISOString` throws. */
export function safeIsoString(date: Date | null | undefined): string | null {
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

/**
 * Replace the civil day on an appointment start.
 * Incomplete values from the month segment are ignored, so state stays a real date.
 */
export function applyAppointmentCivilDate(startsAt: string, civil: string): string {
  if (!isCompleteCivilDate(civil)) return startsAt;
  const time = startsAt.slice(11, 16);
  const clock = /^\d{2}:\d{2}$/.test(time) ? time : '09:00';
  const next = `${civil}T${clock}`;
  if (!safeLocalDateTime(next)) return startsAt;
  return next;
}
